import type { ResponsePayload } from '@lk/shared';
import type { QuizItem } from './types';

export type Draft =
  | { kind: 'choice'; choiceId: string | null }
  | { kind: 'choices'; choiceIds: string[] }
  | { kind: 'numeric'; raw: string }
  | { kind: 'text'; value: string }
  | { kind: 'order'; sequence: string[] }
  | { kind: 'match'; pairs: Array<[string, string]> };

/**
 * Питання формату `match` приходить пласким списком варіантів (так у контракті).
 * Домовленість про поділ на колонки — тут, в одному місці, і вона терпима:
 *   · якщо id починаються з `left:`/`l:` і `right:`/`r:` — беремо їх;
 *   · інакше просто ділимо список навпіл.
 */
export function splitMatchChoices(choices: QuizItem['choices']): {
  left: NonNullable<QuizItem['choices']>;
  right: NonNullable<QuizItem['choices']>;
} {
  const all = choices ?? [];
  const isLeft = (id: string): boolean => /^(left:|l:)/i.test(id);
  const isRight = (id: string): boolean => /^(right:|r:)/i.test(id);

  const tagged = all.filter((choice) => isLeft(choice.id) || isRight(choice.id));
  if (tagged.length === all.length && all.length > 0) {
    return {
      left: all.filter((choice) => isLeft(choice.id)),
      right: all.filter((choice) => isRight(choice.id)),
    };
  }

  const half = Math.ceil(all.length / 2);
  return { left: all.slice(0, half), right: all.slice(half) };
}

export function emptyDraft(item: QuizItem): Draft {
  switch (item.format) {
    case 'multi':
      return { kind: 'choices', choiceIds: [] };
    case 'numeric':
      return { kind: 'numeric', raw: '' };
    case 'text':
      return { kind: 'text', value: '' };
    case 'order':
      return { kind: 'order', sequence: [] };
    case 'match':
      return { kind: 'match', pairs: [] };
    case 'cloze':
      // cloze буває і з варіантами, і з вільним вводом.
      return item.choices && item.choices.length > 0
        ? { kind: 'choice', choiceId: null }
        : { kind: 'text', value: '' };
    case 'mcq':
    default:
      return { kind: 'choice', choiceId: null };
  }
}

export function isDraftReady(draft: Draft, item: QuizItem): boolean {
  switch (draft.kind) {
    case 'choice':
      return draft.choiceId !== null;
    case 'choices':
      return draft.choiceIds.length > 0;
    case 'numeric':
      return draft.raw.trim() !== '' && Number.isFinite(parseNumeric(draft.raw));
    case 'text':
      return draft.value.trim().length > 0;
    case 'order':
      return draft.sequence.length === (item.choices?.length ?? 0) && draft.sequence.length > 0;
    case 'match': {
      const { left } = splitMatchChoices(item.choices);
      return draft.pairs.length === left.length && left.length > 0;
    }
    default:
      return false;
  }
}

/**
 * Дитина може ввести «3,5» — кома для неї природніша за крапку.
 * Порожній рядок навмисно дає NaN, а не 0: інакше незаповнене поле
 * перетворилося б на відповідь «нуль» і зарахувалося як помилка.
 */
export function parseNumeric(raw: string): number {
  const normalized = raw.replace(',', '.').replace(/\s+/g, '');
  if (normalized === '' || normalized === '-' || normalized === '.') return Number.NaN;
  return Number(normalized);
}

export function draftToResponse(draft: Draft): ResponsePayload | null {
  switch (draft.kind) {
    case 'choice':
      return draft.choiceId ? { kind: 'choice', choiceId: draft.choiceId } : null;
    case 'choices':
      return draft.choiceIds.length ? { kind: 'choices', choiceIds: draft.choiceIds } : null;
    case 'numeric': {
      const value = parseNumeric(draft.raw);
      return Number.isFinite(value) ? { kind: 'numeric', value } : null;
    }
    case 'text':
      return draft.value.trim() ? { kind: 'text', value: draft.value.trim() } : null;
    case 'order':
      return draft.sequence.length ? { kind: 'order', sequence: draft.sequence } : null;
    case 'match':
      return draft.pairs.length ? { kind: 'match', pairs: draft.pairs } : null;
    default:
      return null;
  }
}

/** Що саме показати в підсумку як «твоя відповідь». */
export function describeResponse(response: ResponsePayload, item: QuizItem): string {
  const textById = new Map((item.choices ?? []).map((choice) => [choice.id, choice.text]));
  switch (response.kind) {
    case 'choice':
      return textById.get(response.choiceId) ?? response.choiceId;
    case 'choices':
      return response.choiceIds.map((id) => textById.get(id) ?? id).join(', ');
    case 'numeric':
      return `${response.value}${item.unit ? ` ${item.unit}` : ''}`;
    case 'text':
      return response.value;
    case 'order':
      return response.sequence.map((id) => textById.get(id) ?? id).join(' → ');
    case 'match':
      return response.pairs
        .map(([l, r]) => `${textById.get(l) ?? l} — ${textById.get(r) ?? r}`)
        .join('; ');
    case 'timeout':
      return 'час вийшов';
    default:
      return '—';
  }
}
